import { CheckCircle, House, Key } from '@phosphor-icons/react';
import { authMessage, passwordProblem } from '@shh/shared';
import { Button, Heading, Icon, PasswordField, Surface, Text, buttonStyles } from '@shh/ui';
import {
  applyActionCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { accountText } from '../../i18n/account';
import { firebase } from '../../lib/firebase';
import { locale } from '../../lib/locale';
import { startText } from '../../i18n/start';
import { fill } from '../../i18n/fill';

type State =
  | { step: 'checking' }
  | { step: 'new-password'; email: string }
  | { step: 'verified' }
  | { step: 'email-changed' }
  | { step: 'failed'; message: string };

/**
 * Our own landing page for the links in account mails (?mode=…&oobCode=…), so a reset or
 * confirmation ends inside the app instead of on a Firebase page. Works signed in and out;
 * from 1.15 the mails we send ourselves point here.
 */
export function AccountActionPage() {
  const { auth } = firebase();
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const code = params.get('oobCode') ?? '';
  const navigate = useNavigate();
  const [state, setState] = useState<State>(() =>
    mode === 'resetPassword' || mode === 'verifyEmail' || mode === 'verifyAndChangeEmail'
      ? { step: 'checking' }
      : { step: 'failed', message: authMessage('auth/invalid-action-code', locale) ?? '' },
  );
  const [password, setPassword] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  // StrictMode runs effects twice in development; a verify code works only once.
  const started = useRef(false);

  const failed = (e: unknown) =>
    setState({
      step: 'failed',
      message: authMessage((e as { code?: string }).code ?? '', locale) ?? '',
    });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (mode === 'resetPassword')
      void verifyPasswordResetCode(auth, code).then(
        (email) => setState({ step: 'new-password', email }),
        failed,
      );
    else if (mode === 'verifyEmail')
      void applyActionCode(auth, code)
        .then(() => auth.currentUser?.reload())
        .then(() => setState({ step: 'verified' }), failed);
    // "E-Mail ändern" (1.17): Firebase signs every device out – sign in again with the new address
    else if (mode === 'verifyAndChangeEmail')
      void applyActionCode(auth, code)
        .then(() => signOut(auth))
        .then(() => setState({ step: 'email-changed' }), failed);
  }, [auth, mode, code]);

  const problem = passwordProblem(password, locale);

  const save = async (email: string) => {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(undefined);
    try {
      await confirmPasswordReset(auth, code, password);
      await signInWithEmailAndPassword(auth, email, password);
      void navigate('/', { replace: true });
    } catch (e) {
      setError(authMessage((e as { code?: string }).code ?? '', locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-narrow flex-col justify-center gap-section px-safe py-section">
      <span className="grid size-avatar-lg place-items-center rounded-card bg-action-primary text-text-on-action">
        <Icon icon={House} size="xl" weight="fill" />
      </span>

      {state.step === 'checking' && (
        <p role="status" className="text-text-secondary">
          {startText.moment}
        </p>
      )}

      {state.step === 'new-password' && (
        <Surface className="flex flex-col gap-gap-lg">
          <header className="flex flex-col gap-gap-sm">
            <Heading level={1} size="xl">
              {startText.newPassword}
            </Heading>
            <Text tone="secondary">{fill(startText.forEmail, <strong>{state.email}</strong>)}</Text>
          </header>
          <form
            className="flex flex-col gap-gap-md"
            onSubmit={(e) => {
              e.preventDefault();
              void save(state.email);
            }}
          >
            {/* Lets password managers store the new password under the right account. */}
            <input type="hidden" name="username" autoComplete="username" value={state.email} />
            <PasswordField
              label={startText.newPasswordLabel}
              autoComplete="new-password"
              description={startText.min8}
              value={password}
              onChange={setPassword}
              isRequired
              isInvalid={tried && !!problem}
              errorMessage={problem}
            />
            <Button type="submit" size="lg" icon={Key} isDisabled={busy || !password}>
              {startText.savePassword}
            </Button>
            {error && (
              <p role="alert" className="text-sm font-semibold text-feedback-danger">
                {error}
              </p>
            )}
          </form>
        </Surface>
      )}

      {state.step === 'verified' && (
        <Surface className="flex flex-col gap-gap-lg">
          <Heading level={1} size="xl">
            <span className="inline-flex items-center gap-gap-sm">
              <Icon icon={CheckCircle} size="lg" weight="fill" className="text-feedback-success" />
              {startText.verified}
            </span>
          </Heading>
          <Text tone="secondary">{startText.verifiedText}</Text>
          <Link to="/" replace className={buttonStyles({ size: 'lg' })}>
            {startText.toApp}
          </Link>
        </Surface>
      )}

      {state.step === 'email-changed' && (
        <Surface className="flex flex-col gap-gap-lg">
          <Heading level={1} size="xl">
            <span className="inline-flex items-center gap-gap-sm">
              <Icon icon={CheckCircle} size="lg" weight="fill" className="text-feedback-success" />
              {accountText.changedTitle}
            </span>
          </Heading>
          <Text tone="secondary">{accountText.changedText}</Text>
          <Link to="/" replace className={buttonStyles({ size: 'lg' })}>
            {accountText.toLogin}
          </Link>
        </Surface>
      )}

      {state.step === 'failed' && (
        <Surface className="flex flex-col gap-gap-lg">
          <Heading level={1} size="xl">
            {startText.notWorked}
          </Heading>
          <p role="alert" className="text-text-secondary">
            {state.message}
          </p>
          <Link to="/" replace className={buttonStyles({ size: 'lg' })}>
            {startText.toSignin}
          </Link>
        </Surface>
      )}
    </main>
  );
}
